import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import './ReviewsCarousel.css';

const COLS = 3;
const ITEMS_PER_PAGE = 6; // 3 cols × 2 rows

/* ─── Relative time helper ──────────────────────────────── */
const getRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr; // fallback: show raw string if unparseable
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const ReviewCard = ({ review }) => {
  const initials = review.reviewer?.displayName
    ? review.reviewer.displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="review-card">
      <div className="review-card-top">
        {review.reviewer?.profilePhotoUrl ? (
          <img className="review-avatar-img" src={review.reviewer.profilePhotoUrl} alt={review.reviewer.displayName} />
        ) : (
          <div className="review-avatar-initials">{initials}</div>
        )}
        <div>
          <p className="review-name">{review.reviewer?.displayName}</p>
          <p className="review-time">{getRelativeTime(review.updateTime)}</p>
        </div>
      </div>
      <p className="review-text">{review.comment}</p>
      <div className="review-footer">
        <span className="review-stars">
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              style={{
                color: i < review.starRating ? '#f59e0b' : '#d1d5db',
                fontSize: '1.1rem',
                lineHeight: 1,
              }}
            >★</span>
          ))}
        </span>
        <GoogleIcon />
      </div>
    </div>
  );
};

/* ─── Responsive column/row count ───────────────────────── */
function useResponsiveLayout() {
  const compute = () => {
    if (typeof window === 'undefined') return { cols: 3, rows: 2 };
    const w = window.innerWidth;
    if (w <= 640) return { cols: 1, rows: 2 };   // phones: 1 col, 2 reviews/page
    if (w <= 1024) return { cols: 2, rows: 2 };  // tablets: 2 cols, 4 reviews/page
    return { cols: 3, rows: 2 };                 // desktop: 3 cols, 6 reviews/page
  };

  const [layout, setLayout] = useState(compute);

  useEffect(() => {
    const onResize = () => setLayout(compute());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return layout;
}

const ReviewsCarousel = ({ reviews = [] }) => {
  const [page, setPage] = useState(0);
  const timerRef = useRef(null);
  const { cols, rows } = useResponsiveLayout();
  const itemsPerPage = cols * rows;
  const pageRefs = useRef([]);
  const [wrapperHeight, setWrapperHeight] = useState(null);

  const pages = useMemo(() => {
    const result = [];
    for (let i = 0; i < reviews.length; i += itemsPerPage) {
      result.push(reviews.slice(i, i + itemsPerPage));
    }
    return result;
  }, [reviews, itemsPerPage]);

  const totalPages = pages.length;

  useLayoutEffect(() => {
    if (cols !== 1) {
      setWrapperHeight(null);
      return;
    }
    const measure = () => {
      const el = pageRefs.current[page];
      if (el) setWrapperHeight(el.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [page, pages, cols]);

  
  // Keep page index in range when cols/rows change on resize
  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [totalPages, page]);

  const goTo = (p) => setPage(Math.max(0, Math.min(p, totalPages - 1)));

  const startAuto = () => {
    if (totalPages <= 1) return;
    timerRef.current = setInterval(() => setPage((p) => (p + 1) % totalPages), 20000);
  };
  const stopAuto = () => clearInterval(timerRef.current);

  useEffect(() => { startAuto(); return stopAuto; }, [totalPages]);

  const handleNav = (dir) => { stopAuto(); goTo(page + dir); startAuto(); };

  const colRevs = (pageRevs) => Array.from({ length: cols }, (_, ci) =>
    pageRevs.filter((_, i) => i % cols === ci)
  );

  if (totalPages === 0) return null;

  return (
    <div className="reviews-carousel">
      <div
        className="reviews-track-wrapper"
        style={wrapperHeight != null ? { height: `${wrapperHeight}px` } : undefined}
      >
        <div
          className="reviews-track"
          style={{ transform: `translateX(-${page * 100}%)` }}
        >
          {pages.map((pageRevs, pi) => (
            <div
              key={pi}
              className="reviews-page"
              ref={(el) => { pageRefs.current[pi] = el; }}
            >
              {colRevs(pageRevs).map((col, ci) => (
                <div key={ci} className="reviews-col">
                  {col.map((r, ri) => <ReviewCard key={ri} review={r} />)}
                </div>
              ))}
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <>
            <button
              className="reviews-mobile-arrow reviews-mobile-arrow--prev"
              onClick={() => handleNav(-1)}
              disabled={page === 0}
              aria-label="Previous reviews"
            >‹</button>
            <button
              className="reviews-mobile-arrow reviews-mobile-arrow--next"
              onClick={() => handleNav(1)}
              disabled={page === totalPages - 1}
              aria-label="Next reviews"
            >›</button>
          </>
        )}
      </div>

      {totalPages > 1 && (
        <div className="reviews-controls">
          <button className="reviews-nav-btn" onClick={() => handleNav(-1)} disabled={page === 0}>‹</button>
          <div className="reviews-dots">
            {pages.map((_, i) => (
              <button
                key={i}
                className={`reviews-dot ${i === page ? 'active' : ''}`}
                onClick={() => { stopAuto(); goTo(i); startAuto(); }}
              />
            ))}
          </div>
          <button className="reviews-nav-btn" onClick={() => handleNav(1)} disabled={page === totalPages - 1}>›</button>
        </div>
      )}
    </div>
  );
};

export default ReviewsCarousel;